import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import InvitationApi from '@/api/invitationApi';
import { InvitationAccept, InvitationCreate } from '@/schema/invitation';

export const useInvitations = (page: number, size: number, clinicId?: string) =>
  useQuery({
    queryKey: ['invitations', page, size, clinicId ?? ''],
    queryFn: () => InvitationApi.getAll(page, size, clinicId),
  });

export const useCreateInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: InvitationCreate) => InvitationApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });
};

export const useRevokeInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => InvitationApi.revoke(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });
};

export const useResendInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => InvitationApi.resend(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
    },
  });
};

export const useDeleteInvitation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => InvitationApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitations'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
    },
  });
};

/** Public — used by the invite landing page before anyone is signed in. */
export const useInvitationPreview = (token?: string) =>
  useQuery({
    queryKey: ['invitation-preview', token],
    queryFn: () => InvitationApi.preview(token!),
    enabled: Boolean(token),
    retry: false,
  });

export const useAcceptInvitation = () =>
  useMutation({
    mutationFn: ({ token, data }: { token: string; data: InvitationAccept }) =>
      InvitationApi.accept(token, data),
  });
